import { useState } from "react";

import { FormRichText } from "@/components/forms/FormRichText";
import { Heading } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { tiptapJsonToPlainText } from "../../../shared/tiptapText";
import type { PitchRow } from "../campaign";

type NeedsInfoDialogProps = {
    pitch: PitchRow | null;
    actionPending: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (pitch: PitchRow, note: Record<string, unknown>) => void;
};

/** Note emailed to the pitching manager. Sets the pitch to needs-info. */
export function NeedsInfoDialog({ pitch, actionPending, onOpenChange, onSubmit }: NeedsInfoDialogProps) {
    return (
        <Dialog open={pitch != null} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle render={<Heading type="h3" tag="h2" />}>
                        Needs info
                    </DialogTitle>
                    <DialogDescription className="text-foreground">
                        This note is emailed to the pitching manager
                        {pitch?.talentName ? ` for ${atHandle(pitch.talentName)}` : ""}.
                    </DialogDescription>
                </DialogHeader>
                {pitch ? (
                    <NeedsInfoForm
                        key={pitch.id}
                        pitch={pitch}
                        actionPending={actionPending}
                        onCancel={() => onOpenChange(false)}
                        onSubmit={onSubmit}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

function NeedsInfoForm({
    pitch,
    actionPending,
    onCancel,
    onSubmit,
}: {
    pitch: PitchRow;
    actionPending: boolean;
    onCancel: () => void;
    onSubmit: (pitch: PitchRow, note: Record<string, unknown>) => void;
}) {
    const [note, setNote] = useState<Record<string, unknown> | undefined>(pitch.needsInfoNote);
    const [error, setError] = useState<string>();

    function submit() {
        if (!note || !tiptapJsonToPlainText(note)) {
            setError("Add a note for the manager.");
            return;
        }
        setError(undefined);
        onSubmit(pitch, note);
    }

    return (
        <form
            className="space-y-4"
            onSubmit={(event) => {
                event.preventDefault();
                submit();
            }}
        >
            <FormRichText
                label="Note"
                required
                value={note}
                onChange={(next) => {
                    setNote(next);
                    setError(undefined);
                }}
                error={error}
                disabled={actionPending}
            />
            <DialogFooter>
                <Button type="button" variant="secondary" disabled={actionPending} onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={actionPending}>
                    Send
                </Button>
            </DialogFooter>
        </form>
    );
}

function atHandle(name: string) {
    return name.startsWith("@") ? name : `@${name}`;
}
